import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fromUntyped } from "@/integrations/supabase/untyped";
import type { VesselType, WaterIntakeEntry } from "@/types/tracking";

/**
 * useWaterToday — lê entries de água do dia de hoje (Supabase direto).
 *
 * `totalMl` é somado no client a partir das entries (evita query SUM separada).
 *
 * Sprint D bugfix #1 (2026-05-22): hook lifted pra Context (igual
 * useWaterTrackingToggle no Sprint A). Antes: 3 instâncias independentes
 * (Modal, Header gota, HojeView WaterRowHoje) — refetch de uma não propagava
 * pras outras. Agora: WaterTodayProvider monta 1 instância via useWaterTodayState,
 * todos consumers via useWaterToday() compartilham state.
 *
 * 2026-05-26 perf: addOptimistic / replaceOptimistic / removeOptimistic APIs
 * pra updates instantâneos do tally. Antes o fluxo era click → await POST
 * (~800ms) → refetch SELECT (~300ms) → re-render. ~1.5s de "engasgada"
 * percebida pelo user. Agora WaterAddSheet chama addOptimistic IMEDIATAMENTE
 * no click → totalMl jumps instant → quando POST resolve, substitui pelo entry
 * real (com id do servidor), sem refetch full.
 */

const todayLocalISO = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export interface UseWaterTodayResult {
  entries: WaterIntakeEntry[];
  totalMl: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Adiciona uma entry "fantasma" com id temporário e retorna esse id.
   * UI atualiza instantâneo (totalMl jumps). Caller usa o id pra substituir
   * pelo entry real (replaceOptimistic) ou desfazer em caso de erro (removeOptimistic).
   */
  addOptimistic: (amountMl: number, vesselType: VesselType) => string;
  /** Troca um entry temporário pelo entry real vindo do backend. */
  replaceOptimistic: (tempId: string, real: WaterIntakeEntry) => void;
  /** Remove um entry temporário (rollback em caso de erro). */
  removeOptimistic: (tempId: string) => void;
  /**
   * 2026-05-26: Remove entry por id (real ou optimistic) e retorna ela pra
   * eventual restore (rollback de undo otimista). Caller usa restoreEntry
   * em caso de erro do server DELETE.
   */
  removeById: (id: string) => WaterIntakeEntry | undefined;
  /** Re-adiciona uma entry removida (rollback do undo otimista). */
  restoreEntry: (entry: WaterIntakeEntry) => void;
}

/**
 * Internal IO-heavy hook — usado SÓ pelo Provider e por tests que validam
 * Supabase read direto. Componentes consomem via `useWaterToday()` (context).
 */
export const useWaterTodayState = (ioEnabled = true): UseWaterTodayResult => {
  const [entries, setEntries] = useState<WaterIntakeEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(ioEnabled);
  const [error, setError] = useState<string | null>(null);
  // Mirror síncrono de `entries` — necessário pro `removeById` retornar a
  // entry removida síncronamente. Sem mirror, o read dependeria do timing do
  // setEntries updater, que sob React Concurrent / startTransition pode ser
  // deferido. Mantém sincronizado via useEffect[entries] (executa no commit).
  const entriesRef = useRef<WaterIntakeEntry[]>([]);
  const hasLoadedRef = useRef(false);
  const mountedDayRef = useRef<string>(todayLocalISO());
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const today = todayLocalISO();
      const { data, error: supaError } = await fromUntyped("water_intake_log")
        .select("id, auth_id, amount_ml, vessel_type, intake_date, added_at")
        .eq("intake_date", today)
        .order("added_at", { ascending: true });

      if (supaError) {
        hasLoadedRef.current = false;
        setError(supaError.message || "Erro ao buscar consumo de água");
        setEntries([]);
        return;
      }
      // 2026-05-26 fix code-review HIGH: preservar entries optimistic
      // in-flight (id começa com "_opt_") quando refetch sobrescreve o array.
      // Sem isso, race entre POST in-flight + refetch concorrente (ex:
      // visibilitychange, day-roll, undoLast) wipava o optimistic e
      // replaceOptimistic virava noop. Server data segue correto, mas
      // UI ficava stale até próximo refetch organic.
      setEntries((prev) => {
        const pending = prev.filter((e) => e.id.startsWith("_opt_"));
        const fresh = (data as WaterIntakeEntry[]) || [];
        return pending.length > 0 ? [...fresh, ...pending] : fresh;
      });
      hasLoadedRef.current = true;
    } catch (err) {
      hasLoadedRef.current = false;
      const msg = err instanceof Error ? err.message : "Erro inesperado";
      setError(msg);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ioEnabled) {
      setLoading(false);
      return;
    }
    const today = todayLocalISO();
    if (hasLoadedRef.current && mountedDayRef.current === today) return;
    mountedDayRef.current = today;
    void fetchEntries();
  }, [fetchEntries, ioEnabled]);

  useEffect(() => {
    const checkDayRoll = () => {
      if (!ioEnabled) return;
      const currentDay = todayLocalISO();
      if (currentDay !== mountedDayRef.current) {
        mountedDayRef.current = currentDay;
        void fetchEntries();
      }
    };
    const interval = window.setInterval(checkDayRoll, 60_000);
    document.addEventListener("visibilitychange", checkDayRoll);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", checkDayRoll);
    };
  }, [fetchEntries, ioEnabled]);

  const totalMl = entries.reduce(
    (acc, entry) => acc + (Number(entry.amount_ml) || 0),
    0
  );

  // 2026-05-26 perf: optimistic add. Cria entry com prefix "_opt_" no id
  // pra distinguir do real (UUIDs do Postgres não têm esse prefix). Caller
  // usa replaceOptimistic quando o POST retorna ou removeOptimistic em erro.
  const addOptimistic = useCallback(
    (amountMl: number, vesselType: VesselType): string => {
      const tempId = `_opt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const optimistic: WaterIntakeEntry = {
        id: tempId,
        auth_id: "", // não usado em display; será substituído pelo entry real
        amount_ml: amountMl,
        vessel_type: vesselType,
        intake_date: todayLocalISO(),
        added_at: new Date().toISOString(),
      };
      setEntries((prev) => [...prev, optimistic]);
      return tempId;
    },
    [],
  );

  const replaceOptimistic = useCallback(
    (tempId: string, real: WaterIntakeEntry) => {
      setEntries((prev) =>
        prev.map((e) => (e.id === tempId ? real : e)),
      );
    },
    [],
  );

  const removeOptimistic = useCallback((tempId: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== tempId));
  }, []);

  // 2026-05-26: APIs pra undo optimistic. handleUndoLast no WaterAddSheet
  // remove o entry imediatamente (UI responde instant), chama o backend em
  // paralelo, e restaura se der erro.
  //
  // Concurrent-safe (code-review MEDIUM 2026-05-26): lê do ref mirror
  // ANTES de setEntries pra capturar `removed` síncrono. setEntries com
  // functional updater preserva ordem em caso de outras updates em flight.
  const removeById = useCallback(
    (id: string): WaterIntakeEntry | undefined => {
      const current = entriesRef.current;
      const idx = current.findIndex((e) => e.id === id);
      if (idx === -1) return undefined;
      const removed = current[idx];
      setEntries((prev) => {
        const pIdx = prev.findIndex((e) => e.id === id);
        if (pIdx === -1) return prev;
        return [...prev.slice(0, pIdx), ...prev.slice(pIdx + 1)];
      });
      return removed;
    },
    [],
  );

  const restoreEntry = useCallback((entry: WaterIntakeEntry) => {
    setEntries((prev) => [...prev, entry]);
  }, []);

  // 2026-05-26 perf: useMemo evita criar objeto novo de value a cada render
  // do Provider. Sem isso todos consumers (WaterDropHeaderIcon no header global,
  // HojeView WaterRowHoje, WaterAddSheet) re-renderizavam mesmo quando nenhum
  // dos campos primários mudou. As 3 callbacks (addOptimistic/replace/remove)
  // já são useCallback com deps vazias = refs estáveis.
  return useMemo(
    () => ({
      entries,
      totalMl,
      loading,
      error,
      refetch: fetchEntries,
      addOptimistic,
      replaceOptimistic,
      removeOptimistic,
      removeById,
      restoreEntry,
    }),
    [
      entries,
      totalMl,
      loading,
      error,
      fetchEntries,
      addOptimistic,
      replaceOptimistic,
      removeOptimistic,
      removeById,
      restoreEntry,
    ],
  );
};

// ── Context + Provider + public hook ──────────────────────────────────────

/**
 * Exportado na SV-FUNC para que um consumidor OPCIONAL possa perguntar "existe
 * provider?" sem provocar o throw de `useWaterToday`. O Sistema Vivo usa isso:
 * a tela do plano não pode deixar de mostrar o cardápio porque o provider de
 * água não está montado. Quem precisa da água de verdade continua chamando
 * `useWaterToday()`, que segue estourando — o contrato antigo está intacto.
 */
export const WaterTodayContext = createContext<UseWaterTodayResult | null>(null);

export const WaterTodayProvider: React.FC<{
  children: React.ReactNode;
  enabled?: boolean;
}> = ({
  children,
  enabled = true,
}) => {
  const value = useWaterTodayState(enabled);
  return React.createElement(
    WaterTodayContext.Provider,
    { value },
    children,
  );
};

/**
 * Hook público — Header gota / WaterRowHoje / WaterAddSheet consomem aqui.
 * Single instance via Provider → refetch de qualquer caller propaga pra todos.
 */
export const useWaterToday = (): UseWaterTodayResult => {
  const ctx = useContext(WaterTodayContext);
  if (!ctx) {
    throw new Error(
      "useWaterToday deve ser usado dentro de <WaterTodayProvider>",
    );
  }
  return ctx;
};
